function n(value) {
  const num = Number(value || 0);
  return Number.isFinite(num) ? num : 0;
}

function money(value) {
  return n(value).toLocaleString("es-MX", {
    style: "currency",
    currency: "MXN",
    maximumFractionDigits: 2,
  });
}

function pct(value) {
  return `${n(value).toFixed(1)}%`;
}

function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function nowLabel() {
  return new Date().toLocaleString("es-MX", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getResumenPayload(response) {
  return response?.resumen || response?.data?.resumen || response?.item?.resumen || {};
}

function getConfigPayload(response) {
  return response?.config || response?.data?.config || response?.item?.config || {};
}

function getEstadoFinanciero(row) {
  if (!row.hasFinance) return "Sin modelo";
  if (row.utilidad < 0) return "No rentable";
  if (row.ingresos <= 0 && row.costos > 0) return "Consume caja";
  if (row.margen < 10 && row.ingresos > 0) return "Margen bajo";
  if (row.utilidad === 0 && row.costos === 0) return "Sin movimiento";
  return "Rentable";
}

function getAccionSugerida(row) {
  if (!row.hasFinance) return "Definir modelo financiero";
  if (row.utilidad < 0) return "Salir / renegociar";
  if (row.ingresos <= 0 && row.costos > 0) return "Revisar continuidad";
  if (row.margen < 10 && row.ingresos > 0) return "Ajustar margen";
  if (row.bloqueo) return "Desbloquear ejecución";
  if (row.riesgo === "Alto" || row.riesgo === "Crítico") return "Atención directiva";
  return "Mantener";
}

function toneByState(state) {
  if (state === "Rentable") return "good";
  if (state === "No rentable") return "bad";
  if (state === "Consume caja" || state === "Margen bajo") return "warn";
  return "neutral";
}

function groupByPhase(rows) {
  const map = new Map();

  rows.forEach((row) => {
    const fase = row.fase || "Sin fase";
    map.set(fase, (map.get(fase) || 0) + 1);
  });

  return [...map.entries()]
    .map(([fase, total]) => ({ fase, total }))
    .sort((a, b) => b.total - a.total);
}

function getPortfolioFlags(rows) {
  const rentables = rows.filter((r) => getEstadoFinanciero(r) === "Rentable").length;
  const noRentables = rows.filter((r) => getEstadoFinanciero(r) === "No rentable").length;
  const consumeCaja = rows.filter((r) => getEstadoFinanciero(r) === "Consume caja").length;
  const sinModelo = rows.filter((r) => getEstadoFinanciero(r) === "Sin modelo").length;
  const bloqueados = rows.filter((r) => String(r.fase || "").toLowerCase() === "bloqueado" || r.bloqueo).length;
  const riesgoAlto = rows.filter((r) => r.riesgo === "Alto" || r.riesgo === "Crítico").length;

  return { rentables, noRentables, consumeCaja, sinModelo, bloqueados, riesgoAlto };
}

function getPortfolioDiagnosis(totals, flags) {
  if (flags.noRentables > 0) {
    return {
      title: "Requiere decisión directiva",
      text: "Existen proyectos con señal financiera negativa. Deben evaluarse salida, renegociación o reducción de alcance.",
      tone: "bad",
    };
  }

  if (flags.consumeCaja > 0 || flags.bloqueados > 0 || flags.riesgoAlto > 0) {
    return {
      title: "Portafolio en atención operativa",
      text: "La rentabilidad general es positiva, pero existen focos que pueden comprometer margen, entrega o continuidad.",
      tone: "warn",
    };
  }

  if (totals.margen >= 30) {
    return {
      title: "Portafolio saludable",
      text: "El portafolio mantiene margen favorable. Se recomienda conservar foco en ejecución, cobranza y control de costos.",
      tone: "good",
    };
  }

  return {
    title: "Portafolio estable con margen limitado",
    text: "El portafolio opera en positivo. Conviene priorizar proyectos con mayor utilidad y reducir dispersión operativa.",
    tone: "neutral",
  };
}

function barWidth(value, max) {
  const width = max > 0 ? Math.abs(n(value)) / max * 100 : 0;
  return Math.max(3, Math.min(100, width));
}

function buildFinancialBars(rows) {
  const items = [...rows]
    .filter((r) => r.hasFinance)
    .sort((a, b) => b.utilidad - a.utilidad)
    .slice(0, 8);

  const max = Math.max(1, ...items.map((item) => Math.abs(n(item.utilidad))));

  if (!items.length) {
    return `<div class="empty-state">Sin datos financieros suficientes para ranking.</div>`;
  }

  return items.map((row, index) => {
    const state = getEstadoFinanciero(row);
    const tone = toneByState(state);
    const width = barWidth(row.utilidad, max);

    return `
      <article class="rank-row">
        <div class="rank-index">${String(index + 1).padStart(2, "0")}</div>
        <div class="rank-content">
          <div class="rank-head">
            <strong>${escapeHtml(row.nombre)}</strong>
            <span>${money(row.utilidad)}</span>
          </div>
          <div class="rank-sub">
            <small>${escapeHtml(row.cliente || "Sin cliente")}</small>
            <small>${escapeHtml(state)} · ${pct(row.margen)}</small>
          </div>
          <div class="paper-track">
            <div class="paper-bar ${tone}" style="width:${width}%"></div>
          </div>
        </div>
      </article>
    `;
  }).join("");
}

function buildCostIncomeComparison(rows) {
  const items = [...rows]
    .filter((r) => r.hasFinance)
    .sort((a, b) => b.ingresos - a.ingresos)
    .slice(0, 8);

  const max = Math.max(
    1,
    ...items.flatMap((item) => [Math.abs(n(item.ingresos)), Math.abs(n(item.costos)), Math.abs(n(item.utilidad))])
  );

  if (!items.length) {
    return `<div class="empty-state">Sin datos financieros suficientes para comparar.</div>`;
  }

  return items.map((row) => {
    return `
      <article class="compare-row">
        <header>
          <strong>${escapeHtml(row.nombre)}</strong>
          <span>${escapeHtml(getEstadoFinanciero(row))}</span>
        </header>

        <div class="compare-metric">
          <small>Ingresos</small>
          <div class="paper-track"><div class="paper-bar ink" style="width:${barWidth(row.ingresos, max)}%"></div></div>
          <b>${money(row.ingresos)}</b>
        </div>

        <div class="compare-metric">
          <small>Costos</small>
          <div class="paper-track"><div class="paper-bar cost" style="width:${barWidth(row.costos, max)}%"></div></div>
          <b>${money(row.costos)}</b>
        </div>

        <div class="compare-metric">
          <small>Utilidad</small>
          <div class="paper-track"><div class="paper-bar ${row.utilidad >= 0 ? "good" : "bad"}" style="width:${barWidth(row.utilidad, max)}%"></div></div>
          <b>${money(row.utilidad)}</b>
        </div>
      </article>
    `;
  }).join("");
}

function buildPhaseDistribution(phaseRows) {
  const max = Math.max(1, ...phaseRows.map((item) => n(item.total)));

  return phaseRows.map((item) => `
    <article class="phase-row">
      <span>${escapeHtml(item.fase)}</span>
      <div class="paper-track">
        <div class="paper-bar ink" style="width:${barWidth(item.total, max)}%"></div>
      </div>
      <strong>${item.total}</strong>
    </article>
  `).join("");
}

function buildWatchlist(rows) {
  const candidates = [...rows]
    .map((row) => ({
      ...row,
      estado: getEstadoFinanciero(row),
      accion: getAccionSugerida(row),
    }))
    .sort((a, b) => {
      const weight = (row) => {
        if (row.estado === "No rentable") return 1;
        if (row.estado === "Consume caja") return 2;
        if (row.estado === "Margen bajo") return 3;
        if (row.bloqueo) return 4;
        if (row.riesgo === "Alto" || row.riesgo === "Crítico") return 5;
        if (!row.hasFinance) return 6;
        return 9;
      };

      return weight(a) - weight(b) || a.utilidad - b.utilidad;
    })
    .slice(0, 6);

  if (!candidates.length) {
    return `<div class="empty-state">Sin proyectos para watchlist.</div>`;
  }

  return candidates.map((row) => {
    const tone = toneByState(row.estado);
    return `
      <article class="watch-card ${tone}">
        <div>
          <span>${escapeHtml(row.estado)}</span>
          <strong>${escapeHtml(row.nombre)}</strong>
          <small>${escapeHtml(row.cliente || "Sin cliente")} · ${escapeHtml(row.fase || "Sin fase")}</small>
        </div>

        <div class="watch-numbers">
          <b>${money(row.utilidad)}</b>
          <small>${pct(row.margen)}</small>
        </div>

        <footer>${escapeHtml(row.accion)}</footer>
      </article>
    `;
  }).join("");
}

function buildDecisionMatrix(rows) {
  const ordered = [...rows]
    .map((row) => ({
      ...row,
      estado: getEstadoFinanciero(row),
      accion: getAccionSugerida(row),
    }))
    .sort((a, b) => b.utilidad - a.utilidad);

  return ordered.map((row) => {
    const tone = toneByState(row.estado);
    return `
      <tr>
        <td>
          <strong>${escapeHtml(row.nombre)}</strong>
          <small>${escapeHtml(row.cliente || "Sin cliente")}</small>
        </td>
        <td>${money(row.ingresos)}</td>
        <td>${money(row.costos)}</td>
        <td>${money(row.utilidad)}</td>
        <td>${pct(row.margen)}</td>
        <td><span class="status ${tone}">${escapeHtml(row.estado)}</span></td>
        <td>${escapeHtml(row.fase || "Sin fase")}</td>
        <td>${escapeHtml(row.accion)}</td>
      </tr>
    `;
  }).join("");
}

function buildReportHtml(model) {
  const {
    generatedAt,
    totals,
    rows,
    phaseRows,
    flags,
  } = model;

  const diagnosis = getPortfolioDiagnosis(totals, flags, rows);

  const html = `
<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8" />
<title>Informe Ejecutivo de Portafolio</title>
<style>
  * { box-sizing: border-box; }

  body {
    margin: 0;
    background: #e7ebf0;
    color: #101828;
    font-family: Arial, Helvetica, sans-serif;
  }

  .shell {
    width: 1320px;
    margin: 22px auto;
    background: #ffffff;
    border: 1px solid #cfd8e3;
  }

  .topbar {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    padding: 18px 24px;
    border-bottom: 1px solid #cfd8e3;
    background: #ffffff;
  }

  .doc-code {
    color: #667085;
    font-size: 10px;
    font-weight: 900;
    letter-spacing: .18em;
    text-transform: uppercase;
  }

  .actions {
    display: flex;
    gap: 8px;
  }

  .actions button {
    height: 36px;
    border: 1px solid #101828;
    background: #ffffff;
    color: #101828;
    padding: 0 14px;
    font-size: 11px;
    font-weight: 900;
    cursor: pointer;
  }

  .actions button:first-child {
    background: #101828;
    color: #ffffff;
  }

  .cover {
    display: grid;
    grid-template-columns: 1.25fr .75fr;
    border-bottom: 1px solid #cfd8e3;
  }

  .cover-main {
    padding: 34px 36px 30px;
    border-right: 1px solid #cfd8e3;
  }

  .cover-main h1 {
    max-width: 740px;
    margin: 0;
    color: #101828;
    font-size: 38px;
    line-height: 1.02;
    font-weight: 900;
    letter-spacing: -.04em;
  }

  .cover-main p {
    max-width: 760px;
    margin: 16px 0 0;
    color: #475467;
    font-size: 14px;
    line-height: 1.45;
    font-weight: 700;
  }

  .diagnosis {
    margin-top: 26px;
    padding: 15px 16px;
    border: 1px solid #cfd8e3;
    border-left: 5px solid #667085;
    background: #f8fafc;
  }

  .diagnosis.good { border-left-color: #166534; }
  .diagnosis.warn { border-left-color: #b45309; }
  .diagnosis.bad { border-left-color: #b42318; }

  .diagnosis span {
    display: block;
    color: #667085;
    font-size: 9px;
    font-weight: 900;
    letter-spacing: .14em;
    text-transform: uppercase;
    margin-bottom: 6px;
  }

  .diagnosis strong {
    display: block;
    color: #101828;
    font-size: 16px;
    font-weight: 900;
  }

  .diagnosis p {
    margin: 6px 0 0;
    color: #475467;
    font-size: 12px;
    line-height: 1.35;
  }

  .cover-side {
    display: grid;
    grid-template-columns: 1fr;
  }

  .cover-side article {
    padding: 22px 24px;
    border-bottom: 1px solid #cfd8e3;
  }

  .cover-side article:last-child {
    border-bottom: 0;
  }

  .cover-side span {
    display: block;
    color: #667085;
    font-size: 10px;
    font-weight: 900;
    letter-spacing: .14em;
    text-transform: uppercase;
    margin-bottom: 10px;
  }

  .cover-side strong {
    display: block;
    color: #101828;
    font-size: 30px;
    font-weight: 900;
    line-height: 1;
  }

  .kpis {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    border-bottom: 1px solid #cfd8e3;
  }

  .kpi {
    min-height: 112px;
    padding: 18px;
    border-right: 1px solid #cfd8e3;
    background: #ffffff;
  }

  .kpi:last-child {
    border-right: 0;
  }

  .kpi.dark {
    background: #101828;
    color: #ffffff;
  }

  .kpi span {
    display: block;
    color: #667085;
    font-size: 9px;
    font-weight: 900;
    letter-spacing: .14em;
    text-transform: uppercase;
    margin-bottom: 12px;
  }

  .kpi.dark span {
    color: rgba(255,255,255,.7);
  }

  .kpi strong {
    display: block;
    color: inherit;
    font-size: 27px;
    line-height: 1;
    font-weight: 900;
    letter-spacing: -.03em;
  }

  .content {
    padding: 24px;
  }

  .section {
    margin-bottom: 22px;
    border: 1px solid #cfd8e3;
    background: #ffffff;
  }

  .section-head {
    display: grid;
    grid-template-columns: 48px 1fr;
    border-bottom: 1px solid #cfd8e3;
  }

  .section-head b {
    display: grid;
    place-items: center;
    background: #101828;
    color: #ffffff;
    font-size: 12px;
    font-weight: 900;
  }

  .section-head div {
    padding: 13px 16px;
  }

  .section-head h2 {
    margin: 0;
    color: #101828;
    font-size: 17px;
    font-weight: 900;
  }

  .section-head p {
    margin: 4px 0 0;
    color: #667085;
    font-size: 11px;
    font-weight: 700;
  }

  .section-body {
    padding: 16px;
  }

  .two-cols {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
  }

  .three-cols {
    display: grid;
    grid-template-columns: 1.1fr 1fr .9fr;
    gap: 14px;
  }

  .panel {
    border: 1px solid #d7dee8;
    background: #fcfcfd;
    padding: 15px;
  }

  .panel h3 {
    margin: 0 0 14px;
    color: #101828;
    font-size: 13px;
    font-weight: 900;
  }

  .rank-row {
    display: grid;
    grid-template-columns: 34px 1fr;
    gap: 10px;
    padding: 10px 0;
    border-bottom: 1px solid #eaecf0;
  }

  .rank-row:last-child {
    border-bottom: 0;
  }

  .rank-index {
    color: #98a2b3;
    font-size: 11px;
    font-weight: 900;
  }

  .rank-head,
  .rank-sub {
    display: flex;
    justify-content: space-between;
    gap: 12px;
  }

  .rank-head strong {
    color: #101828;
    font-size: 11px;
    font-weight: 900;
  }

  .rank-head span {
    color: #101828;
    font-size: 11px;
    font-weight: 900;
  }

  .rank-sub {
    margin: 4px 0 7px;
  }

  .rank-sub small {
    color: #667085;
    font-size: 10px;
    font-weight: 700;
  }

  .paper-track {
    height: 7px;
    background: #e6ebf2;
    overflow: hidden;
  }

  .paper-bar {
    height: 100%;
    background: #101828;
  }

  .paper-bar.good { background: #166534; }
  .paper-bar.bad { background: #b42318; }
  .paper-bar.warn { background: #b45309; }
  .paper-bar.neutral { background: #667085; }
  .paper-bar.ink { background: #101828; }
  .paper-bar.cost { background: #8a1f2d; }

  .compare-row {
    padding: 11px 0;
    border-bottom: 1px solid #eaecf0;
  }

  .compare-row:last-child {
    border-bottom: 0;
  }

  .compare-row header {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 8px;
  }

  .compare-row header strong {
    font-size: 11px;
    font-weight: 900;
    color: #101828;
  }

  .compare-row header span {
    font-size: 9px;
    font-weight: 900;
    color: #667085;
    text-transform: uppercase;
  }

  .compare-metric {
    display: grid;
    grid-template-columns: 68px 1fr 92px;
    gap: 8px;
    align-items: center;
    margin-bottom: 6px;
  }

  .compare-metric small,
  .phase-row span {
    color: #667085;
    font-size: 9px;
    font-weight: 900;
    text-transform: uppercase;
  }

  .compare-metric b,
  .phase-row strong {
    color: #101828;
    font-size: 10px;
    font-weight: 900;
    text-align: right;
  }

  .phase-row {
    display: grid;
    grid-template-columns: 92px 1fr 38px;
    gap: 8px;
    align-items: center;
    padding: 9px 0;
    border-bottom: 1px solid #eaecf0;
  }

  .phase-row:last-child {
    border-bottom: 0;
  }

  .watch-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
  }

  .watch-card {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 12px;
    min-height: 122px;
    padding: 14px;
    border: 1px solid #d7dee8;
    border-left: 5px solid #667085;
    background: #ffffff;
  }

  .watch-card.good { border-left-color: #166534; }
  .watch-card.warn { border-left-color: #b45309; }
  .watch-card.bad { border-left-color: #b42318; }

  .watch-card span {
    display: block;
    color: #667085;
    font-size: 9px;
    font-weight: 900;
    letter-spacing: .08em;
    text-transform: uppercase;
    margin-bottom: 7px;
  }

  .watch-card strong {
    display: block;
    color: #101828;
    font-size: 13px;
    font-weight: 900;
    line-height: 1.18;
  }

  .watch-card small {
    display: block;
    color: #667085;
    font-size: 10px;
    font-weight: 700;
    margin-top: 5px;
  }

  .watch-numbers {
    text-align: right;
  }

  .watch-numbers b {
    display: block;
    color: #101828;
    font-size: 12px;
    font-weight: 900;
  }

  .watch-card footer {
    grid-column: 1 / -1;
    align-self: end;
    padding-top: 9px;
    border-top: 1px solid #eaecf0;
    color: #101828;
    font-size: 11px;
    font-weight: 900;
  }

  table {
    width: 100%;
    border-collapse: collapse;
  }

  thead th {
    padding: 10px 8px;
    text-align: left;
    color: #667085;
    font-size: 9px;
    font-weight: 900;
    letter-spacing: .10em;
    text-transform: uppercase;
    border-bottom: 2px solid #101828;
  }

  tbody td {
    padding: 10px 8px;
    color: #344054;
    font-size: 11px;
    border-bottom: 1px solid #eaecf0;
    vertical-align: top;
  }

  tbody td strong {
    display: block;
    color: #101828;
    font-size: 11px;
    font-weight: 900;
  }

  tbody td small {
    display: block;
    color: #667085;
    font-size: 10px;
    margin-top: 3px;
  }

  .status {
    display: inline-block;
    padding: 4px 6px;
    border: 1px solid #cfd8e3;
    color: #344054;
    font-size: 9px;
    font-weight: 900;
    text-transform: uppercase;
    white-space: nowrap;
  }

  .status.good { border-color: #166534; color: #166534; }
  .status.warn { border-color: #b45309; color: #b45309; }
  .status.bad { border-color: #b42318; color: #b42318; }

  .empty-state {
    padding: 18px;
    color: #667085;
    border: 1px dashed #98a2b3;
    font-size: 12px;
    font-weight: 700;
  }

  @page {
    size: A4 landscape;
    margin: 8mm;
  }

  @media print {
    body {
      background: #ffffff;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .shell {
      width: auto;
      margin: 0;
      border: 0;
    }

    .actions {
      display: none;
    }

    .cover,
    .kpis,
    .section {
      break-inside: avoid;
      page-break-inside: avoid;
    }

    .content {
      padding: 12px 0 0;
    }
  }
</style>
</head>
<body>
  <div class="shell">
    <div class="topbar">
      <div class="doc-code">IP DEPOT · PROJECT COMMAND CENTER · INFORME INTERNO DE PORTAFOLIO</div>
      <div class="actions">
        <button onclick="window.print()">Descargar PDF</button>
        <button onclick="window.close()">Cerrar</button>
      </div>
    </div>

    <section class="cover">
      <div class="cover-main">
        <h1>Informe ejecutivo de portafolio</h1>
        <p>
          Lectura directiva de rentabilidad, vida operativa, riesgo, continuidad y proyectos que requieren decisión.
        </p>

        <div class="diagnosis ${diagnosis.tone}">
          <span>Dictamen</span>
          <strong>${escapeHtml(diagnosis.title)}</strong>
          <p>${escapeHtml(diagnosis.text)}</p>
        </div>
      </div>

      <aside class="cover-side">
        <article>
          <span>Fecha de corte</span>
          <strong>${escapeHtml(generatedAt)}</strong>
        </article>
        <article>
          <span>Proyectos analizados</span>
          <strong>${rows.length}</strong>
        </article>
        <article>
          <span>Margen global</span>
          <strong>${pct(totals.margen)}</strong>
        </article>
      </aside>
    </section>

    <section class="kpis">
      <article class="kpi dark">
        <span>Ingresos</span>
        <strong>${money(totals.ingresos)}</strong>
      </article>
      <article class="kpi dark">
        <span>Costos</span>
        <strong>${money(totals.costos)}</strong>
      </article>
      <article class="kpi">
        <span>Utilidad neta</span>
        <strong>${money(totals.utilidad)}</strong>
      </article>
      <article class="kpi">
        <span>Rentables</span>
        <strong>${flags.rentables}</strong>
      </article>
      <article class="kpi">
        <span>Requieren decisión</span>
        <strong>${flags.noRentables + flags.consumeCaja + flags.bloqueados + flags.riesgoAlto}</strong>
      </article>
    </section>

    <main class="content">
      <section class="section">
        <div class="section-head">
          <b>01</b>
          <div>
            <h2>Decisiones recomendadas</h2>
            <p>Continuidad, renegociación, salida o corrección operativa.</p>
          </div>
        </div>
        <div class="section-body">
          <div class="watch-grid">
            ${buildWatchlist(rows)}
          </div>
        </div>
      </section>

      <section class="section">
        <div class="section-head">
          <b>02</b>
          <div>
            <h2>Lectura financiera comparativa</h2>
            <p>Utilidad, ingresos, costos y concentración operativa.</p>
          </div>
        </div>
        <div class="section-body">
          <div class="three-cols">
            <div class="panel">
              <h3>Ranking de utilidad</h3>
              ${buildFinancialBars(rows)}
            </div>

            <div class="panel">
              <h3>Ingresos / costos / utilidad</h3>
              ${buildCostIncomeComparison(rows)}
            </div>

            <div class="panel">
              <h3>Concentración por fase</h3>
              ${buildPhaseDistribution(phaseRows)}
            </div>
          </div>
        </div>
      </section>

      <section class="section">
        <div class="section-head">
          <b>03</b>
          <div>
            <h2>Matriz ejecutiva</h2>
            <p>Priorización de portafolio para seguimiento directivo.</p>
          </div>
        </div>
        <div class="section-body">
          <table>
            <thead>
              <tr>
                <th>Proyecto</th>
                <th>Ingresos</th>
                <th>Costos</th>
                <th>Utilidad</th>
                <th>Margen</th>
                <th>Estado</th>
                <th>Fase</th>
                <th>Acción sugerida</th>
              </tr>
            </thead>
            <tbody>
              ${buildDecisionMatrix(rows)}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  </div>
</body>
</html>
  `;

  return html;
}

export async function openPortfolioExecutiveReport({ apiRequest, proyectos = [] }) {
  const uniqueProjects = (Array.isArray(proyectos) ? proyectos : [])
    .filter(Boolean)
    .map((p) => ({
      id: p.id || p.proyectoId,
      nombre: p.nombre || "Proyecto sin nombre",
      cliente: p.cliente || "",
      fase: p.fase || p.etapa || "",
      riesgo: p.riesgo || "",
      bloqueo: p.bloqueo || "",
      avance: n(p.avance),
    }))
    .filter((p) => p.id);

  if (!uniqueProjects.length) {
    alert("No hay proyectos para generar el reporte.");
    return;
  }

  const settled = await Promise.allSettled(
    uniqueProjects.map(async (project) => {
      try {
        const response = await apiRequest(
          "/proyectos/" + encodeURIComponent(project.id) + "/finanzas/resumen"
        );

        const resumen = getResumenPayload(response);
        const config = getConfigPayload(response);

        return {
          project,
          resumen,
          config,
          ok: true,
        };
      } catch (error) {
        return {
          project,
          resumen: {},
          config: {},
          ok: false,
          error: error?.message || "Sin datos financieros",
        };
      }
    })
  );

  const rows = settled.map((result) => {
    const payload = result.status === "fulfilled"
      ? result.value
      : {
          project: {},
          resumen: {},
          config: {},
          ok: false,
          error: result.reason?.message || "Error",
        };

    const project = payload.project || {};
    const resumen = payload.resumen || {};

    const ingresos = n(resumen.ingresos);
    const costos = n(resumen.invertido);
    const utilidad = n(resumen.utilidad);
    const margen = n(resumen.margen);
    const hasFinance =
      payload.ok &&
      (
        ingresos !== 0 ||
        costos !== 0 ||
        utilidad !== 0 ||
        Object.keys(payload.config || {}).length > 0
      );

    return {
      id: project.id,
      nombre: project.nombre,
      cliente: project.cliente,
      fase: project.fase,
      riesgo: project.riesgo,
      bloqueo: project.bloqueo,
      avance: project.avance,
      ingresos,
      costos,
      utilidad,
      margen,
      hasFinance,
      estadoResumen: resumen.estado || "",
      config: payload.config || {},
      rawResumen: resumen,
    };
  });

  const totals = rows.reduce(
    (acc, row) => {
      acc.ingresos += n(row.ingresos);
      acc.costos += n(row.costos);
      acc.utilidad += n(row.utilidad);
      return acc;
    },
    { ingresos: 0, costos: 0, utilidad: 0, margen: 0 }
  );

  totals.margen = totals.ingresos > 0 ? (totals.utilidad / totals.ingresos) * 100 : 0;

  const phaseRows = groupByPhase(rows);
  const flags = getPortfolioFlags(rows);

  const html = buildReportHtml({
    generatedAt: nowLabel(),
    totals,
    rows,
    phaseRows,
    flags,
  });

  const win = window.open("", "_blank", "width=1480,height=980");

  if (!win) {
    alert("El navegador bloqueó la ventana del reporte. Permite pop-ups para este sitio.");
    return;
  }

  win.document.open();
  win.document.write(html);
  win.document.close();
  win.focus();
}
